import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, Square, Clock, Loader, User, Users, Sparkles, CheckCircle, AlertCircle, ChevronRight, MessageSquare, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { contactService, aiService } from '../services/api';
import { useToast, ToastContainer } from '../componets/Toast';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/API_URL';
import PaywallModal from '../subscription/PaywallModal';
const AITranscriber = () => {
  const navigate = useNavigate();
  const { toasts, removeToast, showSuccess, showError, showWarning, showInfo } = useToast();
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeatureInfo, setPaywallFeatureInfo] = useState(null);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [audioContext, setAudioContext] = useState(null);
  const [analyser, setAnalyser] = useState(null);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [transcript, setTranscript] = useState('');
  const [summary, setSummary] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [detectedContact, setDetectedContact] = useState(null);

  // Contact selection
  const [contacts, setContacts] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState('new');
  const [showResults, setShowResults] = useState(false);

  // Past transcripts
  const [showTranscripts, setShowTranscripts] = useState(false);
  const [pastTranscripts, setPastTranscripts] = useState([]);
  const [loadingTranscripts, setLoadingTranscripts] = useState(false);
  const [expandedTranscript, setExpandedTranscript] = useState(null);

  // Refs
  const timerRef = useRef(null);
  const animationRef = useRef(null);

  const MAX_RECORDING_TIME = 600; // 10 minutes in seconds

  const styles = {
    page: {
      padding: '2rem',
      maxWidth: '900px',
      margin: '0 auto',
      minHeight: '100vh',
    },
    header: {
      marginBottom: '2rem',
      textAlign: 'center',
    },
    title: {
      fontSize: '2rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 800,
      color: '#0f172a',
      marginBottom: '0.5rem',
    },
    subtitle: {
      color: '#475569',
      fontSize: '1rem',
    },
    card: {
      background: '#ffffff',
      borderRadius: '20px',
      padding: '2.5rem',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: '1px solid #e2e8f0',
      marginBottom: '2rem',
    },
    visualizerCard: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      borderRadius: '20px',
      padding: '3rem',
      boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)',
      marginBottom: '2rem',
      textAlign: 'center',
      position: 'relative',
      minHeight: '500px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    timer: {
      fontSize: '3rem',
      fontWeight: 700,
      color: 'white',
      fontFamily: "'Syne', sans-serif",
      marginBottom: '1rem',
    },
    statusText: {
      fontSize: '1.1rem',
      color: 'rgba(255, 255, 255, 0.9)',
      marginBottom: '2rem',
      fontWeight: 500,
    },
    warningText: {
      fontSize: '0.9rem',
      color: '#fef3c7',
      marginTop: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      justifyContent: 'center',
    },
    controlButtons: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '1rem 2rem',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      minWidth: '180px',
    },
    primaryButton: {
      background: 'white',
      color: '#667eea',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    },
    dangerButton: {
      background: '#ef4444',
      color: 'white',
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)',
    },
    secondaryButton: {
      background: 'rgba(255, 255, 255, 0.2)',
      color: 'white',
      border: '2px solid rgba(255, 255, 255, 0.3)',
    },
    formGroup: {
      marginBottom: '1.5rem',
    },
    label: {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#0f172a',
      fontWeight: 600,
      fontSize: '0.95rem',
    },
    select: {
      width: '100%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '1rem',
      fontFamily: 'inherit',
      background: '#f8fafc',
      outline: 'none',
      cursor: 'pointer',
      transition: 'all 200ms',
    },
    processingContainer: {
      textAlign: 'center',
      padding: '3rem 2rem',
    },
    processingIcon: {
      marginBottom: '1.5rem',
      color: '#667eea',
    },
    processingTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '0.5rem',
      fontFamily: "'Syne', sans-serif",
    },
    processingStep: {
      fontSize: '1rem',
      color: '#64748b',
      marginBottom: '2rem',
    },
    loadingDots: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
      justifyContent: 'center',
    },
    loadingDot: {
      width: '12px',
      height: '12px',
      borderRadius: '50%',
      background: '#667eea',
    },
    resultsCard: {
      background: '#f8fafc',
      borderRadius: '16px',
      padding: '1.5rem',
      marginBottom: '1.5rem',
      border: '2px solid #e2e8f0',
    },
    resultsTitle: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    transcript: {
      fontSize: '0.95rem',
      lineHeight: 1.7,
      color: '#475569',
      whiteSpace: 'pre-wrap',
      maxHeight: '300px',
      overflowY: 'auto',
      padding: '1rem',
      background: 'white',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
    },
    summary: {
      fontSize: '0.95rem',
      lineHeight: 1.7,
      color: '#475569',
    },
    keywordsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.75rem',
    },
    keyword: {
      padding: '0.5rem 1rem',
      background: '#eff6ff',
      color: '#1e40af',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 600,
    },
    contactCard: {
      background: 'white',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      padding: '1.25rem',
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    contactInfo: {
      flex: 1,
    },
    contactName: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    contactMeta: {
      fontSize: '0.85rem',
      color: '#64748b',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.35rem 0.75rem',
      borderRadius: '6px',
      fontSize: '0.75rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    newBadge: {
      background: '#dcfce7',
      color: '#166534',
    },
    existingBadge: {
      background: '#dbeafe',
      color: '#1e40af',
    },
    actionButtons: {
      display: 'flex',
      gap: '1rem',
      marginTop: '2rem',
    },
    fullWidthButton: {
      flex: 1,
    },
    viewTranscriptsButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.875rem 1.5rem',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      fontSize: '0.95rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#667eea',
      width: '100%',
      marginTop: '1rem',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem',
    },
    modalContent: {
      background: 'white',
      borderRadius: '20px',
      padding: '2rem',
      maxWidth: '800px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      position: 'relative',
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      paddingBottom: '1rem',
      borderBottom: '2px solid #e2e8f0',
    },
    modalTitle: {
      fontSize: '1.5rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
    },
    closeButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#64748b',
      padding: '0.5rem',
      borderRadius: '8px',
      transition: 'all 200ms',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    transcriptItem: {
      background: '#f8fafc',
      border: '2px solid #e2e8f0',
      borderRadius: '12px',
      padding: '1.25rem',
      marginBottom: '1rem',
      cursor: 'pointer',
      transition: 'all 200ms',
    },
    transcriptItemExpanded: {
      background: '#ffffff',
      borderColor: '#667eea',
    },
    transcriptHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '0.5rem',
    },
    transcriptContactName: {
      fontSize: '1.05rem',
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '0.25rem',
    },
    transcriptDate: {
      fontSize: '0.85rem',
      color: '#64748b',
    },
    transcriptSummaryPreview: {
      fontSize: '0.9rem',
      color: '#64748b',
      lineHeight: 1.6,
      marginTop: '0.75rem',
    },
    transcriptExpandedContent: {
      marginTop: '1.5rem',
      paddingTop: '1.5rem',
      borderTop: '2px solid #e2e8f0',
    },
    transcriptSection: {
      marginBottom: '1.5rem',
    },
    transcriptSectionTitle: {
      fontSize: '0.95rem',
      fontWeight: 700,
      color: '#0f172a',
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    transcriptFullText: {
      fontSize: '0.9rem',
      lineHeight: 1.7,
      color: '#475569',
      whiteSpace: 'pre-wrap',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      maxHeight: '300px',
      overflowY: 'auto',
    },
    emptyTranscripts: {
      textAlign: 'center',
      padding: '3rem 2rem',
      color: '#64748b',
    },
  };

  useEffect(() => {
    loadContacts();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (audioContext) audioContext.close();
    };
  }, []);

  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          if (prev >= MAX_RECORDING_TIME - 1) {
            handleStopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isRecording, isPaused]);

  const loadContacts = async () => {
    try {
      const data = await contactService.getContacts();
      setContacts(data.contacts);
    } catch (error) {
      console.error('Failed to load contacts:', error);
      showError('Failed to load contacts');
    }
  };

  const loadPastTranscripts = async () => {
    setLoadingTranscripts(true);
    try {
      const response = await fetch(`${API_URL}/api/call-transcripts`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to load transcripts');
      }
      
      const data = await response.json();
      setPastTranscripts(data.transcripts);
      setShowTranscripts(true);
    } catch (error) {
      console.error('Failed to load transcripts:', error);
      showError('Failed to load past transcripts');
    } finally {
      setLoadingTranscripts(false);
    }
  };

  const loadFullTranscript = async (transcriptId) => {
    try {
      const response = await fetch(`${API_URL}/api/call-transcripts/${transcriptId}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('Failed to load transcript details');
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to load transcript details:', error);
      showError('Failed to load transcript details');
      return null;
    }
  };

  const handleTranscriptClick = async (transcript) => {
    if (expandedTranscript?.id === transcript.id) {
      setExpandedTranscript(null);
    } else {
      const fullData = await loadFullTranscript(transcript.id);
      if (fullData) {
        setExpandedTranscript(fullData);
      }
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const initializeAudioVisualization = async (stream) => {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyserNode = ctx.createAnalyser();
    const source = ctx.createMediaStreamSource(stream);
    
    source.connect(analyserNode);
    analyserNode.fftSize = 256;
    
    setAudioContext(ctx);
    setAnalyser(analyserNode);
    
    visualize(analyserNode);
  };

  const visualize = (analyserNode) => {
    const bufferLength = analyserNode.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animationRef.current = requestAnimationFrame(draw);
      analyserNode.getByteFrequencyData(dataArray);

      // Calculate average volume
      const average = dataArray.reduce((a, b) => a + b) / bufferLength;
      setAudioLevel(average / 255);
    };

    draw();
  };

  const handleStartRecording = async () => {
    // Check feature access first
    try {
      const response = await fetch(`${API_URL}/api/subscription/check-access/transcribe`, {
        credentials: 'include',
      });
      
      const data = await response.json();
      
      if (!data.has_access) {
        // Show paywall
        setPaywallFeatureInfo(data);
        setShowPaywall(true);
        return;
      }
      
      // Has access, proceed with recording
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      
      const chunks = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

      recorder.onstop = () => {
        setAudioChunks(chunks);
        stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      
      await initializeAudioVisualization(stream);
      
      showInfo('Recording started - speak naturally!');
    } catch (error) {
      console.error('Failed to start recording:', error);
      
      if (error.message && error.message.includes('feature_access_required')) {
        // Show paywall
        setPaywallFeatureInfo(error);
        setShowPaywall(true);
      } else {
        showError('Failed to access microphone. Please check permissions.');
      }
    }
  };
  const handleStopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
      setIsPaused(false);
      
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContext) {
        audioContext.close();
      }

      if (recordingTime < 5) {
        showWarning('Recording is very short. Consider recording a longer conversation for better results.');
      }
    }
  };

  const handleProcessRecording = async () => {
    if (audioChunks.length === 0) {
      showError('No recording to process');
      return;
    }

    setIsProcessing(true);
    setProcessingStep('Transcribing audio...');

    try {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      
      // Step 1: Transcribe
      const formData = new FormData();
      formData.append('audio', audioBlob);
      formData.append('contact_selection', selectedContactId);

      const transcribeResponse = await fetch(`${API_URL}/api/transcribe-call`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!transcribeResponse.ok) {
        throw new Error('Transcription failed');
      }

      const transcribeData = await transcribeResponse.json();
      setTranscript(transcribeData.transcript);

      // Step 2: Process with Claude
      setProcessingStep('Analyzing conversation...');

      const processResponse = await fetch(`${API_URL}/api/process-call-transcript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          transcript: transcribeData.transcript,
          contact_selection: selectedContactId,
          duration_seconds: recordingTime,
        }),
      });

      if (!processResponse.ok) {
        throw new Error('Processing failed');
      }

      const processData = await processResponse.json();
      
      setSummary(processData.summary);
      setKeywords(processData.keywords);
      setDetectedContact(processData.contact);
      setShowResults(true);
      
      showSuccess('Call processed successfully! 🎉');
      
      // Reload contacts if new one was created
      if (processData.contact_created) {
        await loadContacts();
      }

    } catch (error) {
      console.error('Failed to process recording:', error);
      showError('Failed to process recording. Please try again.');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  

  const handleReset = () => {
    setAudioChunks([]);
    setRecordingTime(0);
    setTranscript('');
    setSummary('');
    setKeywords([]);
    setDetectedContact(null);
    setShowResults(false);
    setSelectedContactId('new');
  };

  const handleViewContact = () => {
    if (detectedContact) {
      navigate(`/dashboard/contacts/${detectedContact.id}`);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const LoadingDots = () => (
    <div style={styles.loadingDots}>
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
      />
    </div>
  );

  const remainingTime = MAX_RECORDING_TIME - recordingTime;
  const isNearLimit = remainingTime <= 60;

  return (
    <div style={styles.page}>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        featureInfo={paywallFeatureInfo}
        sourcePage="transcriber"
        onPurchaseComplete={() => {
          setShowPaywall(false);
          showSuccess('Purchase successful! You can now use this feature.');
        }}
      />
      <div style={styles.header}>
        <h1 style={styles.title}>AI Call Transcriber</h1>
        <p style={styles.subtitle}>
          Record networking calls and get instant transcripts, summaries, and insights
        </p>
      </div>

      {/* Pre-recording setup */}
      {!isRecording && audioChunks.length === 0 && !showResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.card}
        >
          <div style={styles.formGroup}>
            <label style={styles.label}>
              <User size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
              Who are you meeting with?
            </label>
            <select
              value={selectedContactId}
              onChange={(e) => setSelectedContactId(e.target.value)}
              style={styles.select}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.boxShadow = 'none';
              }}
            >
              <option value="new">🆕 New Contact (AI will detect details)</option>
              {contacts.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.name} {contact.company ? `- ${contact.company}` : ''}
                </option>
              ))}
            </select>
          </div>

          <div style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '12px',
            padding: '1rem',
            marginBottom: '2rem',
          }}>
            <div style={{ fontSize: '0.9rem', color: '#1e40af', lineHeight: 1.6 }}>
              <strong>💡 Tips for best results:</strong>
              <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
                <li>Place your device close to both speakers</li>
                <li>Minimize background noise</li>
                <li>Speak clearly and at a moderate pace</li>
                <li>Record for at least 1-2 minutes</li>
                <li>Max recording time: 10 minutes</li>
              </ul>
            </div>
          </div>

          <button
            onClick={handleStartRecording}
            style={{
              ...styles.button,
              ...styles.primaryButton,
              width: '100%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
          >
            <Mic size={20} />
            Start Recording
          </button>

          <button
            onClick={loadPastTranscripts}
            disabled={loadingTranscripts}
            style={styles.viewTranscriptsButton}
            onMouseEnter={(e) => {
              e.target.style.borderColor = '#667eea';
              e.target.style.background = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.target.style.borderColor = '#e2e8f0';
              e.target.style.background = '#ffffff';
            }}
          >
            <Clock size={18} />
            {loadingTranscripts ? 'Loading...' : 'View Past Transcripts'}
          </button>
        </motion.div>
      )}

      {/* Past Transcripts Modal */}
      <AnimatePresence>
        {showTranscripts && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={styles.modal}
            onClick={() => {
              setShowTranscripts(false);
              setExpandedTranscript(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={styles.modalContent}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>Past Call Transcripts</h3>
                <button
                  onClick={() => {
                    setShowTranscripts(false);
                    setExpandedTranscript(null);
                  }}
                  style={styles.closeButton}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'none';
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              {pastTranscripts.length === 0 ? (
                <div style={styles.emptyTranscripts}>
                  <Clock size={48} style={{ color: '#cbd5e1', marginBottom: '1rem' }} />
                  <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                    No transcripts yet
                  </div>
                  <div style={{ fontSize: '0.9rem' }}>
                    Record your first networking call to see transcripts here
                  </div>
                </div>
              ) : (
                <div>
                  {pastTranscripts.map((transcript) => (
                    <div
                      key={transcript.id}
                      onClick={() => handleTranscriptClick(transcript)}
                      style={{
                        ...styles.transcriptItem,
                        ...(expandedTranscript?.id === transcript.id ? styles.transcriptItemExpanded : {}),
                      }}
                      onMouseEnter={(e) => {
                        if (expandedTranscript?.id !== transcript.id) {
                          e.currentTarget.style.borderColor = '#cbd5e1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (expandedTranscript?.id !== transcript.id) {
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }
                      }}
                    >
                      <div style={styles.transcriptHeader}>
                        <div style={{ flex: 1 }}>
                          <div style={styles.transcriptContactName}>
                            {transcript.contact?.name || 'Unknown Contact'}
                          </div>
                          {transcript.contact?.company && (
                            <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.25rem' }}>
                              {transcript.contact.company}
                            </div>
                          )}
                          <div style={styles.transcriptDate}>
                            {formatDate(transcript.recorded_at)} • {Math.floor(transcript.duration_seconds / 60)}m {transcript.duration_seconds % 60}s
                          </div>
                        </div>
                        <motion.div
                          animate={{ rotate: expandedTranscript?.id === transcript.id ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronRight size={20} color="#667eea" />
                        </motion.div>
                      </div>

                      {expandedTranscript?.id !== transcript.id && (
                        <div style={styles.transcriptSummaryPreview}>
                          {transcript.summary?.substring(0, 150)}
                          {transcript.summary?.length > 150 ? '...' : ''}
                        </div>
                      )}

                      {expandedTranscript?.id === transcript.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          style={styles.transcriptExpandedContent}
                        >
                          {/* Summary */}
                          <div style={styles.transcriptSection}>
                            <div style={styles.transcriptSectionTitle}>
                              <Sparkles size={18} />
                              Summary
                            </div>
                            <div style={{ fontSize: '0.95rem', lineHeight: 1.7, color: '#475569' }}>
                              {expandedTranscript.summary}
                            </div>
                          </div>

                          {/* Keywords */}
                          {expandedTranscript.keywords && expandedTranscript.keywords.length > 0 && (
                            <div style={styles.transcriptSection}>
                              <div style={styles.transcriptSectionTitle}>Key Topics</div>
                              <div style={styles.keywordsList}>
                                {expandedTranscript.keywords.map((keyword, idx) => (
                                  <span key={idx} style={styles.keyword}>
                                    {keyword}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Full Transcript */}
                          <div style={styles.transcriptSection}>
                            <div style={styles.transcriptSectionTitle}>
                              <MessageSquare size={18} />
                              Full Transcript
                            </div>
                            <div style={styles.transcriptFullText}>
                              {expandedTranscript.transcript}
                            </div>
                          </div>

                          {/* Action button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/dashboard/contacts/${expandedTranscript.contact?.id}`);
                            }}
                            style={{
                              ...styles.button,
                              ...styles.primaryButton,
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              width: '100%',
                              marginTop: '1rem',
                            }}
                          >
                            <User size={18} />
                            View Contact Details
                          </button>
                        </motion.div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recording in progress - ChatGPT-style visualization */}
      {isRecording && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          style={styles.visualizerCard}
        >
          {/* ChatGPT-style audio-reactive circles */}
          <div style={{
            position: 'relative',
            width: '280px',
            height: '280px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '2rem',
          }}>
            {/* Outer pulsing rings - react to audio */}
            {[0, 1, 2].map((index) => (
              <motion.div
                key={index}
                style={{
                  position: 'absolute',
                  borderRadius: '50%',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                }}
                animate={{
                  width: [200 + (index * 40), 200 + (index * 40) + (audioLevel * 60)],
                  height: [200 + (index * 40), 200 + (index * 40) + (audioLevel * 60)],
                  opacity: [0.6 - (index * 0.15), 0.3 - (index * 0.1)],
                }}
                transition={{
                  duration: 0.3,
                  ease: 'easeOut',
                }}
              />
            ))}

            {/* Main center circle with audio-reactive scale */}
            <motion.div
              style={{
                position: 'relative',
                width: '180px',
                height: '180px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(10px)',
              }}
              animate={{
                scale: 1 + (audioLevel * 0.15),
              }}
              transition={{
                duration: 0.1,
                ease: 'easeOut',
              }}
            >
              {/* Inner glow circle */}
              <motion.div
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)',
                }}
                animate={{
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Microphone icon */}
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <Mic size={64} color="white" strokeWidth={2.5} />
              </motion.div>
            </motion.div>

            {/* Audio level indicator bars (ChatGPT style) */}
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              display: 'flex',
              gap: '4px',
              alignItems: 'flex-end',
              height: '40px',
            }}>
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  style={{
                    width: '6px',
                    borderRadius: '3px',
                    background: 'rgba(255, 255, 255, 0.9)',
                  }}
                  animate={{
                    height: [
                      10 + (Math.random() * 10),
                      10 + (audioLevel * 30) + (Math.random() * 10),
                      10 + (Math.random() * 10),
                    ],
                  }}
                  transition={{
                    duration: 0.3 + (Math.random() * 0.2),
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
              ))}
            </div>
          </div>

          <div style={styles.timer}>{formatTime(recordingTime)}</div>
          <div style={styles.statusText}>
            {audioLevel > 0.3 ? '🎤 Listening...' : '🤫 Listening... (speak up)'}
          </div>

          {isNearLimit && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              style={styles.warningText}
            >
              <Clock size={16} />
              {remainingTime} seconds remaining
            </motion.div>
          )}

          <div style={styles.controlButtons}>
            <button
              onClick={handleStopRecording}
              style={{
                ...styles.button,
                ...styles.dangerButton,
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(239, 68, 68, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
              }}
            >
              <Square size={20} />
              Stop Recording
            </button>
          </div>
        </motion.div>
      )}

      {/* Processing */}
      {isProcessing && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.card}
        >
          <div style={styles.processingContainer}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              style={styles.processingIcon}
            >
              <Loader size={48} />
            </motion.div>
            <div style={styles.processingTitle}>Processing Call...</div>
            <div style={styles.processingStep}>{processingStep}</div>
            <LoadingDots />
          </div>
        </motion.div>
      )}

      {/* Recording complete, ready to process */}
      {!isRecording && audioChunks.length > 0 && !isProcessing && !showResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.card}
        >
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <CheckCircle size={64} color="#10b981" style={{ marginBottom: '1rem' }} />
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem' }}>
              Recording Complete
            </div>
            <div style={{ fontSize: '1rem', color: '#64748b' }}>
              Duration: {formatTime(recordingTime)}
            </div>
          </div>

          <div style={styles.actionButtons}>
            <button
              onClick={handleReset}
              style={{
                ...styles.button,
                ...styles.secondaryButton,
                ...styles.fullWidthButton,
                color: '#64748b',
                border: '2px solid #e2e8f0',
                background: 'white',
              }}
            >
              Discard & Start Over
            </button>
            <button
              onClick={handleProcessRecording}
              style={{
                ...styles.button,
                ...styles.primaryButton,
                ...styles.fullWidthButton,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
              }}
            >
              <Sparkles size={20} />
              Process Call
            </button>
          </div>
        </motion.div>
      )}

      {/* Results */}
      {showResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Contact info */}
          {detectedContact && (
            <div style={styles.card}>
              <div style={styles.resultsTitle}>
                <Users size={20} />
                Contact Identified
              </div>
              <div style={styles.contactCard}>
                <div style={styles.contactInfo}>
                  <div style={styles.contactName}>{detectedContact.name}</div>
                  <div style={styles.contactMeta}>
                    {detectedContact.company && `${detectedContact.company}`}
                    {detectedContact.role && ` • ${detectedContact.role}`}
                  </div>
                </div>
                <span style={{
                  ...styles.badge,
                  ...(detectedContact.is_new ? styles.newBadge : styles.existingBadge),
                }}>
                  {detectedContact.is_new ? 'New Contact' : 'Existing'}
                </span>
              </div>
            </div>
          )}

          {/* Summary */}
          <div style={styles.card}>
            <div style={styles.resultsCard}>
              <div style={styles.resultsTitle}>
                <Sparkles size={20} />
                Call Summary
              </div>
              <div style={styles.summary}>{summary}</div>
            </div>

            {/* Keywords */}
            <div style={styles.resultsCard}>
              <div style={styles.resultsTitle}>Key Topics</div>
              <div style={styles.keywordsList}>
                {keywords.map((keyword, idx) => (
                  <span key={idx} style={styles.keyword}>
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            {/* Full transcript */}
            <div style={styles.resultsCard}>
              <div style={styles.resultsTitle}>Full Transcript</div>
              <div style={styles.transcript}>{transcript}</div>
            </div>
          </div>

          {/* Action buttons */}
          <div style={styles.card}>
            <div style={styles.actionButtons}>
              <button
                onClick={handleReset}
                style={{
                  ...styles.button,
                  ...styles.secondaryButton,
                  ...styles.fullWidthButton,
                  color: '#64748b',
                  border: '2px solid #e2e8f0',
                  background: 'white',
                }}
              >
                Record Another Call
              </button>
              {detectedContact && (
                <button
                  onClick={handleViewContact}
                  style={{
                    ...styles.button,
                    ...styles.primaryButton,
                    ...styles.fullWidthButton,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                  }}
                >
                  <User size={20} />
                  View Contact Details
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AITranscriber;